import { z } from "zod";
import type { HTMLInputTypeAttribute } from "~/types/agent-types";
import { parseBool } from "./basic";

export const createZodSchema = (data: any) => {
  const { groupList, group, table: tables } = data;

  let zodSchema = z.object({});
  let headerFields = z.object({});

  group
    .filter((prop: any) => groupList.includes(prop?.id))
    .forEach((prop: any) => {
      const headerField: any = {};
      const { fields } = prop;
      fields.forEach((field: any) => {
        const { validation } = field;

        let fieldSchema: z.ZodType;
        switch (field.type as HTMLInputTypeAttribute) {
          case "checkbox":
          case "email":
          case "password":
          case "radio":
          case "text":
            {
              fieldSchema = z.string({
                required_error: "Please fill out this field.",
                message: "Please fill out this field",
              });

              const isRequired = parseBool(validation.required as string);
              
              if (!isRequired) {
                fieldSchema = fieldSchema.nullable();
              } else {
                // Ensure the value is not an empty string or null for required fields
                fieldSchema = fieldSchema.refine(
                  (val) => val != null && val.trim().length > 0,
                  {
                    message: "This field cannot be empty",
                  }
                );
              }

              if (validation.regex != null && validation?.regex?.length > 0) {
                fieldSchema = fieldSchema.refine(
                  (val: any) => {
                    if (val != null && val.length > 0) {
                      return new RegExp(validation.regex as string).test(val);
                    }
                    return true;
                  },
                  {
                    message: validation.regexMessage,
                  }
                );
              }

              if (
                validation?.maxLength != null &&
                Number(validation?.maxLength)
              ) {
                fieldSchema = fieldSchema.refine(
                  (val) => {
                    if (val == null && !isRequired) {
                      return true;
                    }
                    return val != null && val.length <= +validation.maxLength;
                  },
                  {
                    message: `${validation.maxLengthMessage}`,
                  }
                );
              }
            }
            break;
          case "number":
            fieldSchema = z.number();
            break;
          case "date":
            {
              fieldSchema = z.string({
                required_error: "Please fill out this field.",
                message: "Please fill out this field",
              });

              const isRequired = parseBool(validation.required as string);

              if (!isRequired) {
                fieldSchema = fieldSchema.nullable();
              } else {
                fieldSchema = fieldSchema.refine(
                  (val) => val != null && val.trim().length > 0,
                  {
                    message: "This field cannot be empty",
                  }
                );
              }

              if (validation.regex != null && validation?.regex?.length > 0) {
                fieldSchema = fieldSchema.refine(
                  (val: any) => {
                    if (val != null && val.length > 0) {
                      return new RegExp(validation.regex as string).test(val);
                    }
                    return true; // Skip regex validation if the field is null or empty
                  },
                  {
                    message: validation.regexMessage,
                  }
                );
              }
            }
            break;
          case "select":
          case "search-select":
            {
              fieldSchema = z.string({
                required_error: "Please fill out this field.",
                message: "Please fill out this field",
              });
              const isRequired = parseBool(validation.required as string);

              if (!isRequired) {
                fieldSchema = fieldSchema.nullable();
              } else {
                // Ensure the value is not an empty string for required fields
                fieldSchema = fieldSchema.refine(
                  (val) => val != null && val.trim().length > 0,
                  {
                    message: "This field cannot be empty",
                  }
                );
              }
            }
            break;
          case "select-autocomplete":
            {
              fieldSchema = z.string({
                required_error: "Please fill out this field.",
                message: "Please fill out this field",
              });
              const isRequired = parseBool(validation.required as string);

              if (!isRequired) {
                fieldSchema = fieldSchema.nullable();
              } else {
                // Ensure the value is not an empty string for required fields
                fieldSchema = fieldSchema.refine(
                  (val) => val != null && val.trim().length > 0,
                  {
                    message: "This field cannot be empty or This value is invalid",
                  }
                );
              }

              if (validation.regex != null && validation?.regex?.length > 0) {
                fieldSchema = fieldSchema.refine(
                  (val: any) => {
                    if (val != null && val.length > 0) {
                      return new RegExp(validation.regex as string).test(val);
                    }
                    return true; // Skip regex validation if the field is null or empty
                  },
                  {
                    message: validation.regexMessage,
                  }
                );
              }

              if (
                validation?.maxLength != null &&
                Number(validation?.maxLength)
              ) {
                fieldSchema = fieldSchema.refine(
                  (val) => {
                    if (val == null && !isRequired) {
                      return true;
                    }
                    return val != null && val.length <= +validation.maxLength;
                  },
                  {
                    message: `${validation.maxLengthMessage}`,
                  }
                );
              }
            }
            break;
          default:
            fieldSchema = z.string();
        }
        headerField[field.id] = fieldSchema;
      });
      headerFields = headerFields.extend(headerField);
    });

  zodSchema = zodSchema.extend({
    header: z.array(headerFields),
  });

  zodSchema = zodSchema.extend({
    table: z.record(z.array(z.record(z.any()))).superRefine((tabs, ctx) => {
      Object.entries(tabs).forEach(([tableName, records]) => {
        const requiredFields = tables
          .find((table: any) => table.id === tableName)
          ?.fields.filter((f: any) =>
            f.validation.required === "true" ? f?.id : null
          );

        const allFields = tables.find(
          (table: any) => table.id === tableName
        )?.fields;

        records?.forEach((record: any, index: number) => {
          // Check required fields for null or empty values
          requiredFields?.forEach((field: any) => {
            if (
              record[field.id] === null ||
              record[field.id] === "" ||
              (parseBool(field.validation.required as string) &&
                record[field.id]?.length === 0)
            ) {
              ctx.addIssue({
                path: [tableName, index, field.id],
                message: field.validation.regexMessage,
                code: z.ZodIssueCode.custom,
              });
            }
          });

          // Check all fields for regex validation
          allFields?.forEach((field: any) => {
            const fieldValue = record[field.id];

            // Only validate regex if the field has a value
            if (
              field.validation.regex != null &&
              field?.validation?.regex?.length > 0
            ) {
              if (
                fieldValue !== null &&
                fieldValue !== "" &&
                !new RegExp(field.validation.regex as string).test(
                  fieldValue as string
                )
              ) {
                ctx.addIssue({
                  path: [tableName, index, field.id],
                  message: field.validation.regexMessage,
                  code: z.ZodIssueCode.custom,
                });
              }
            }
          });
        });
      });
    }),
  });

  return zodSchema;
};

export const createSkeletonSchema = (schema: any) => {
  if (schema instanceof z.ZodObject) {
    const shape = schema.shape;
    const skeleton: any = {};
    for (const key in shape) {
      const subSchema = shape[key];
      if (subSchema instanceof z.ZodObject) {
        skeleton[key] = createSkeletonSchema(subSchema);
      } else if (subSchema instanceof z.ZodArray) {
        skeleton[key] = [];
      } else if (subSchema instanceof z.ZodOptional) {
        skeleton[key] = undefined;
      } else if (subSchema instanceof z.ZodString) {
        skeleton[key] = "";
      } else if (subSchema instanceof z.ZodNumber) {
        skeleton[key] = 0;
      } else {
        skeleton[key] = null;
      }
    }
    return skeleton;
  }
  return null;
};
